import React, { useEffect, useState } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { Navigate, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

interface Piece {
  conversation_id: string;
  type: string;
  status: string;
  deceased_name: string | null;
  created_at: string;
  updated_at: string;
}

interface License {
  id: string;
  status: string;
  deceased_person_name: string;
  created_at: string;
  // Phase 1 business-model-v2 fields (may be null on grandfathered pre-Phase-1 licenses)
  tier?: 'single_tribute' | 'family_memorial' | null;
  writing_activated_at?: string | null;
  has_conversation?: boolean;
  conversation_id?: string | null;
  conversation_type?: string | null;
  conversation_status?: string | null;
  // Phase 6e: Family & Friends licenses can have multiple pieces per deceased
  // person — the full list is surfaced so the UI can show every tribute/eulogy.
  pieces?: Piece[];
}

// Phase 1: a license is locked to one piece if it's a Phase 1+ Single Tribute
// (writing_activated_at set) that already has a conversation. Pre-Phase-1
// licenses (writing_activated_at NULL) are grandfathered and keep the old
// multi-conversation behaviour.
function isPhase1SingleTributeLocked(license: License): boolean {
  return (
    !!license.writing_activated_at &&
    license.tier === 'single_tribute' &&
    !!license.has_conversation
  );
}

// Phase 6e: Family & Friends licenses support multiple pieces per deceased
// person. When the license is family_memorial and already has at least one
// piece, render an expanded card that surfaces every piece plus a
// "Start another tribute" affordance.
function isFamilyMemorialMulti(license: License): boolean {
  return license.tier === 'family_memorial' && !!license.has_conversation;
}

function pieceStatusLabel(status: string): string {
  if (status === 'completed') return 'Completed';
  if (status === 'finalised') return 'Finalised';
  if (status === 'archived') return 'Archived';
  return 'In progress';
}

function formatUpdated(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

const NewProject: React.FC = () => {
  const { isLoaded, isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [selectedType, setSelectedType] = useState<'eulogy' | 'tribute' | null>(null);

  useEffect(() => {
    document.title = 'Cyrano — New Eulogy or Tribute';
    window.scrollTo(0, 0);
  }, []);

  // Fetch existing licenses
  useEffect(() => {
    const fetchLicenses = async () => {
      if (!isLoaded || !isSignedIn) return;
      let activeLicenses: License[] = [];
      try {
        const token = await getToken();
        const resp = await fetch('/api/subscription/status', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (resp.ok) {
          const data = await resp.json();
          activeLicenses = (data.licenses || []).filter(
            (l: License) => l.status === 'active' || l.status === 'completed'
          );
        }
      } catch (err) {
        console.error('Error fetching licenses:', err);
      }

      // If no licenses (or the request failed), send them straight to onboarding.
      // There's nothing useful for them to pick on this page.
      if (activeLicenses.length === 0) {
        navigate('/onboarding?new=true', { replace: true });
        return;
      }

      setLicenses(activeLicenses);
      setLoading(false);
    };
    fetchLicenses();
  }, [isLoaded, isSignedIn, getToken, navigate]);

  // Start conversation for an existing licensed person.
  // Refuses to redirect if user?.id is not yet populated — redirecting
  // without userId drops the user into conversation-interface.html's
  // local-only fallback mode (no DB, template eulogy, lost answers).
  const startForExistingPerson = () => {
    if (!selectedLicense || !selectedType) return;
    if (!user?.id) {
      console.error('startForExistingPerson: user not ready, aborting redirect');
      return;
    }

    const params = new URLSearchParams();
    params.set('type', selectedType);
    params.set('name', selectedLicense.deceased_person_name);
    params.set('autoAdvance', 'true');
    params.set('userId', user.id);
    const userName = [user.firstName, user.lastName].filter(Boolean).join(' ');
    if (userName) params.set('userName', userName);
    const userEmail = user.primaryEmailAddress?.emailAddress || '';
    if (userEmail) params.set('userEmail', userEmail);

    localStorage.setItem('cyrano_onboarding_data', JSON.stringify({
      type: selectedType,
      name: selectedLicense.deceased_person_name,
      userName: userName,
    }));

    window.location.href = `/conversation-interface.html?${params.toString()}`;
  };

  // Phase 1: resume the one existing conversation on a locked Single Tribute license
  const resumeLockedConversation = (license: License) => {
    if (!license.conversation_id) return;
    if (!user?.id) {
      console.error('resumeLockedConversation: user not ready, aborting redirect');
      return;
    }
    const params = new URLSearchParams();
    params.set('sessionId', license.conversation_id);
    params.set('userId', user.id);
    window.location.href = `/conversation-interface.html?${params.toString()}`;
  };

  // Phase 6e: resume an arbitrary piece under a Family & Friends license
  const resumePiece = (conversationId: string) => {
    if (!user?.id) {
      console.error('resumePiece: user not ready, aborting redirect');
      return;
    }
    const params = new URLSearchParams();
    params.set('sessionId', conversationId);
    params.set('userId', user.id);
    window.location.href = `/conversation-interface.html?${params.toString()}`;
  };

  if (!isLoaded || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-background">
        <div className="text-center">
          <img src="/GhostQuillLogo.png" alt="Cyrano" className="w-16 h-16 mx-auto animate-pulse" />
          <p className="text-cool-gray mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  return (
    <div className="min-h-screen bg-warm-background flex flex-col">
      <Header />

      <main className="max-w-[800px] mx-auto px-4 sm:px-6 py-8 flex-1 w-full">

        {/* Step indicator: choosing person or choosing type? */}
        {!selectedLicense ? (
          <>
            {/* STEP 1: Choose who this is for */}
            <div className="text-center mb-10">
              <img
                src="/Assets/200-handdrawn-icons-by-heloholo/png/64/book64.png"
                alt=""
                className="w-16 h-16 mx-auto mb-5"
              />
              <h1 className="font-serif text-[2rem] sm:text-[2.25rem] font-semibold text-space-cadet mb-3">
                Who is this for?
              </h1>
              <p className="text-cool-gray text-lg leading-relaxed max-w-lg mx-auto">
                Create a new eulogy or tribute for someone you've already started writing for, or begin fresh for someone new.
              </p>
            </div>

            {/* Existing licensed people */}
            <div className="space-y-4 mb-8">
              {licenses.map((license) => {
                const locked = isPhase1SingleTributeLocked(license);
                const familyMulti = isFamilyMemorialMulti(license);

                // Phase 6e: Family & Friends multi-piece card — expanded view
                // listing every tribute/eulogy under this license plus a
                // "Start another tribute" affordance. The backend already
                // allows multiple pieces under a family_memorial license; this
                // is the frontend that finally surfaces them.
                if (familyMulti) {
                  const pieces = license.pieces || [];
                  return (
                    <div
                      key={license.id}
                      className="w-full bg-white rounded-2xl p-5 sm:p-7 shadow-[0_4px_12px_rgba(25,40,92,0.08)] border-2 border-cool-gray/15"
                    >
                      <div className="flex items-center gap-5 mb-5">
                        <div className="flex-shrink-0">
                          <img
                            src="/Assets/200-handdrawn-icons-by-heloholo/png/48/love48.png"
                            alt=""
                            className="w-12 h-12"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-serif text-xl font-semibold text-space-cadet truncate">
                            {license.deceased_person_name}
                          </h3>
                          <p className="text-cool-gray text-sm mt-1">
                            Family &amp; Friends license — {pieces.length} {pieces.length === 1 ? 'piece' : 'pieces'}
                          </p>
                        </div>
                      </div>

                      {/* List of existing pieces */}
                      <div className="space-y-2 mb-4">
                        {pieces.map((p) => {
                          const typeLabel = p.type === 'tribute' ? 'Tribute' : 'Eulogy';
                          return (
                            <button
                              key={p.conversation_id}
                              onClick={() => resumePiece(p.conversation_id)}
                              className="w-full flex items-center gap-3 p-3 rounded-lg border border-cool-gray/20 hover:border-space-cadet-primary/50 hover:bg-pink-lavender/5 transition-all duration-200 text-left group"
                            >
                              <img
                                src={p.type === 'tribute'
                                  ? "/Assets/200-handdrawn-icons-by-heloholo/png/48/love48.png"
                                  : "/Assets/200-handdrawn-icons-by-heloholo/png/48/book48.png"}
                                alt=""
                                className="w-8 h-8 flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-space-cadet text-sm">
                                  {typeLabel} · {pieceStatusLabel(p.status)}
                                </div>
                                <div className="text-cool-gray text-xs mt-0.5">
                                  Updated {formatUpdated(p.updated_at)}
                                </div>
                              </div>
                              <img
                                src="/Assets/200-handdrawn-icons-by-heloholo/png/48/arrow_right48.png"
                                alt=""
                                className="w-5 h-5 opacity-40 group-hover:opacity-80 transition-opacity flex-shrink-0"
                              />
                            </button>
                          );
                        })}
                      </div>

                      {/* Start another piece under this license */}
                      <button
                        onClick={() => setSelectedLicense(license)}
                        className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed border-cool-gray/30 hover:border-space-cadet-primary/50 hover:bg-pink-lavender/5 transition-all duration-200 text-space-cadet font-semibold text-sm"
                      >
                        <img
                          src="/Assets/200-handdrawn-icons-by-heloholo/png/48/plus_circle48.png"
                          alt=""
                          className="w-5 h-5"
                        />
                        Start another tribute or eulogy for {license.deceased_person_name}
                      </button>
                    </div>
                  );
                }

                // Single Tribute (locked or grandfathered) — current compact card
                const subtitle = locked
                  ? (license.conversation_status === 'completed'
                      ? 'Your tribute is ready — continue editing'
                      : 'Continue where you left off')
                  : 'Create another eulogy or tribute';
                return (
                  <button
                    key={license.id}
                    onClick={() =>
                      locked ? resumeLockedConversation(license) : setSelectedLicense(license)
                    }
                    className="w-full bg-white rounded-2xl p-5 sm:p-7 shadow-[0_4px_12px_rgba(25,40,92,0.08)] border-2 border-cool-gray/15 hover:border-space-cadet-primary/50 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(25,40,92,0.15)] transition-all duration-300 text-left group"
                  >
                    <div className="flex items-center gap-5">
                      <div className="flex-shrink-0">
                        <img
                          src="/Assets/200-handdrawn-icons-by-heloholo/png/48/love48.png"
                          alt=""
                          className="w-12 h-12"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-serif text-xl font-semibold text-space-cadet group-hover:text-space-cadet-primary transition-colors truncate">
                          {locked ? `Continue with ${license.deceased_person_name}` : license.deceased_person_name}
                        </h3>
                        <p className="text-cool-gray text-sm mt-1">
                          {subtitle}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <img
                          src="/Assets/200-handdrawn-icons-by-heloholo/png/48/arrow_right48.png"
                          alt=""
                          className="w-6 h-6 opacity-40 group-hover:opacity-80 transition-opacity"
                        />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4 my-10">
              <div className="flex-1 h-px bg-cool-gray/20"></div>
              <span className="text-cool-gray text-sm font-medium">or</span>
              <div className="flex-1 h-px bg-cool-gray/20"></div>
            </div>

            {/* Start fresh for someone new */}
            <button
              onClick={() => navigate('/onboarding?new=true')}
              className="w-full bg-white rounded-2xl p-5 sm:p-7 shadow-[0_4px_12px_rgba(25,40,92,0.08)] border-2 border-dashed border-cool-gray/30 hover:border-space-cadet-primary/50 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(25,40,92,0.15)] transition-all duration-300 text-left group"
            >
              <div className="flex items-center gap-5">
                <div className="flex-shrink-0">
                  <img
                    src="/Assets/200-handdrawn-icons-by-heloholo/png/48/plus_circle48.png"
                    alt=""
                    className="w-12 h-12"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif text-xl font-semibold text-space-cadet group-hover:text-space-cadet-primary transition-colors">
                    For someone new
                  </h3>
                  <p className="text-cool-gray text-sm mt-1">
                    Begin a eulogy or tribute for a different loved one
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <img
                    src="/Assets/200-handdrawn-icons-by-heloholo/png/48/arrow_right48.png"
                    alt=""
                    className="w-6 h-6 opacity-40 group-hover:opacity-80 transition-opacity"
                  />
                </div>
              </div>
            </button>

            {/* Back to Dashboard link */}
            <div className="text-center mt-10">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-cool-gray hover:text-space-cadet-primary transition-colors text-sm inline-flex items-center gap-1"
              >
                ← Back to Dashboard
              </button>
            </div>
          </>
        ) : !selectedType ? (
          <>
            {/* STEP 2: Choose eulogy or tribute for this person */}
            <div className="text-center mb-10">
              <img
                src="/Assets/200-handdrawn-icons-by-heloholo/png/64/love64.png"
                alt=""
                className="w-16 h-16 mx-auto mb-5"
              />
              <h1 className="font-serif text-[2rem] sm:text-[2.25rem] font-semibold text-space-cadet mb-3">
                For {selectedLicense.deceased_person_name}
              </h1>
              <p className="text-cool-gray text-lg leading-relaxed max-w-lg mx-auto">
                What would you like to create?
              </p>
            </div>

            {/* Instruction Box */}
            <div className="bg-pink-lavender/10 border-l-4 border-pink-lavender rounded-lg p-5 mb-8 flex items-start gap-4">
              <img
                src="/Assets/200-handdrawn-icons-by-heloholo/png/64/arrow_right64.png"
                alt=""
                className="w-6 h-6 flex-shrink-0 mt-0.5"
              />
              <p className="text-space-cadet font-medium text-base leading-relaxed">
                Please select one option below to continue
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {/* Eulogy Card */}
              <button
                onClick={() => setSelectedType('eulogy')}
                className={`relative rounded-xl p-6 md:p-10 text-left transition-all duration-300 ${
                  selectedType === 'eulogy'
                    ? 'bg-space-cadet-primary/5 shadow-[0_8px_24px_rgba(25,40,92,0.2)]'
                    : 'border-2 border-cool-gray/20 hover:border-space-cadet-primary/50 hover:-translate-y-1 hover:shadow-[0_6px_20px_rgba(25,40,92,0.15)]'
                }`}
                style={{
                  borderWidth: selectedType === 'eulogy' ? '3px' : undefined,
                  borderColor: selectedType === 'eulogy' ? '#19285c' : undefined,
                }}
              >
                <img
                  src="/Assets/200-handdrawn-icons-by-heloholo/png/64/book64.png"
                  alt="Eulogy"
                  className="w-20 h-20 mb-6 block"
                />
                <h3 className="font-serif text-2xl font-semibold text-space-cadet mb-4">
                  A Eulogy
                </h3>
                <p className="text-cool-gray text-base mb-6 leading-relaxed">
                  An overview of a life, covering the journey from birth through to the present. It includes the highlights, milestones, relationships, and achievements that defined them.
                </p>
                <div className="bg-pink-lavender/10 border-l-4 border-pink-lavender p-5 rounded-md">
                  <div className="font-semibold text-space-cadet text-sm mb-2">Best for:</div>
                  <div className="text-cool-gray text-sm leading-relaxed">
                    Telling the complete story of their life. Cyrano will help you structure this journey in a way that feels complete and meaningful.
                  </div>
                </div>
              </button>

              {/* Tribute Card */}
              <button
                onClick={() => setSelectedType('tribute')}
                className={`relative rounded-xl p-6 md:p-10 text-left transition-all duration-300 ${
                  selectedType === 'tribute'
                    ? 'bg-space-cadet-primary/5 shadow-[0_8px_24px_rgba(25,40,92,0.2)]'
                    : 'border-2 border-cool-gray/20 hover:border-space-cadet-primary/50 hover:-translate-y-1 hover:shadow-[0_6px_20px_rgba(25,40,92,0.15)]'
                }`}
                style={{
                  borderWidth: selectedType === 'tribute' ? '3px' : undefined,
                  borderColor: selectedType === 'tribute' ? '#19285c' : undefined,
                }}
              >
                <img
                  src="/Assets/200-handdrawn-icons-by-heloholo/png/64/love64.png"
                  alt="Tribute"
                  className="w-20 h-20 mb-6 block"
                />
                <h3 className="font-serif text-2xl font-semibold text-space-cadet mb-4">
                  A Tribute
                </h3>
                <p className="text-cool-gray text-base mb-6 leading-relaxed">
                  A more personal reflection focusing on your unique relationship with your loved one. It might be a story that captures their personality, a treasured memory, or an expression of what they meant to you.
                </p>
                <div className="bg-pink-lavender/10 border-l-4 border-pink-lavender p-5 rounded-md">
                  <div className="font-semibold text-space-cadet text-sm mb-2">Best for:</div>
                  <div className="text-cool-gray text-sm leading-relaxed">
                    Sharing your personal connection and the ways they touched your life. A tribute is yours and yours alone.
                  </div>
                </div>
              </button>
            </div>

            {/* Back button */}
            <div className="text-center mt-8">
              <button
                onClick={() => { setSelectedLicense(null); setSelectedType(null); }}
                className="text-cool-gray hover:text-space-cadet-primary transition-colors text-sm inline-flex items-center gap-1"
              >
                ← Choose a different person
              </button>
            </div>
          </>
        ) : (
          <>
            {/* STEP 3: Confirm and go */}
            <div className="bg-white rounded-2xl p-5 sm:p-8 md:p-12 shadow-[0_8px_32px_rgba(25,40,92,0.12)] border border-space-cadet/8 text-center">
              <img
                src={selectedType === 'eulogy'
                  ? "/Assets/200-handdrawn-icons-by-heloholo/png/64/book64.png"
                  : "/Assets/200-handdrawn-icons-by-heloholo/png/64/love64.png"
                }
                alt=""
                className="w-20 h-20 mx-auto mb-6"
              />
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-space-cadet mb-4">
                Ready to begin
              </h2>
              <p className="text-cool-gray text-lg leading-relaxed max-w-md mx-auto mb-8">
                You're about to create {selectedType === 'eulogy' ? 'a eulogy' : 'a tribute'} for{' '}
                <span className="font-semibold text-space-cadet">{selectedLicense.deceased_person_name}</span>.
                Cyrano will guide you through a warm conversation to gather your memories.
              </p>

              <button
                onClick={startForExistingPerson}
                className="px-10 py-4 min-h-[52px] rounded-xl font-semibold text-lg transition-all duration-200 hover:shadow-[0_6px_20px_rgba(25,40,92,0.25)] hover:-translate-y-0.5"
                style={{ backgroundColor: '#19285c', color: '#ffffff' }}
              >
                Begin Conversation
              </button>

              <div className="mt-8">
                <button
                  onClick={() => setSelectedType(null)}
                  className="text-cool-gray hover:text-space-cadet-primary transition-colors text-sm inline-flex items-center gap-1"
                >
                  ← Change selection
                </button>
              </div>
            </div>
          </>
        )}

      </main>

      <Footer />
    </div>
  );
};

export default NewProject;
